// ---------------------------------------------------------------------------
// history.js — interned-snapshot undo/redo for the Design Editor.
//
// ONE owner of history. Every mutation the editor makes routes through
// `push()` (see the editor API seam: editor.pushHistory / pushHistoryDebounced).
//
// Two problems this module fixes vs. the old inline implementation:
//   1. Snapshots used to be multi-MB: every step re-serialised every image as
//      base64, so a design with a 2.5k product photo cost ~10 MB per undo step
//      and blew up memory. Here each unique image `src` (data URL) is interned
//      once in a side pool; snapshots reference it by a short sentinel key.
//   2. History was unbounded. Here it is capped (default 40) and the pool is
//      swept of unreferenced entries whenever the cap trims the stack.
//
// Snapshots are stored as JSON STRINGS with image srcs replaced by the sentinel
// `@@imgref:<key>`. `resolve()` re-inflates them for loadFromJSON.
// ---------------------------------------------------------------------------

export function createHistory(canvas, options = {}) {
  const cap = options.cap || 40;
  const getProps = options.serializationProps || (() => []);

  // Per-instance sentinel. resolve() string-replaces this over the whole snapshot,
  // so a user's text/layer name that literally equalled a fixed sentinel would be
  // silently rewritten into image data. A random per-session token makes that
  // collision impossible. The token is [0-9a-z] only — regex-safe. The sentinel
  // never leaves this instance (exportDesignJson embeds real base64, not refs).
  const token = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
  const SENTINEL = `@@imgref_${token}:`;

  // Interning pool: dataURL -> key (dedupe) and key -> dataURL (resolve).
  const keyBySrc = new Map();
  const srcByKey = new Map();
  let nextKey = 1;

  // Each entry: { snap: <JSON string with sentinels>, meta: <object>, metaStr }.
  // `meta` carries editor state that isn't part of the fabric object graph but
  // must survive undo/redo — currently the artboard background color, which we
  // deliberately keep OFF canvas.backgroundColor (fabric would fill the whole
  // pasteboard under zoom; the artboard-only fill is painted in a render hook).
  let stack = [];
  let index = -1;

  const internSrc = (src) => {
    if (typeof src !== 'string') return src;
    // Only intern heavy inline data URLs; leave short remote URLs inline.
    if (!src.startsWith('data:')) return src;
    let key = keyBySrc.get(src);
    if (!key) {
      key = String(nextKey++);
      keyBySrc.set(src, key);
      srcByKey.set(key, src);
    }
    return SENTINEL + key;
  };

  // Walk fabric JSON objects (recursing into groups) and rewrite image srcs.
  const walk = (objects, fn) => {
    if (!Array.isArray(objects)) return;
    objects.forEach((obj) => {
      if (!obj || typeof obj !== 'object') return;
      if (obj.type === 'image' && typeof obj.src === 'string') {
        obj.src = fn(obj.src);
      }
      if (Array.isArray(obj.objects)) walk(obj.objects, fn);
    });
  };

  const serialize = () => {
    const json = canvas.toJSON(getProps());
    walk(json.objects, internSrc);
    return JSON.stringify(json);
  };

  // Rebuild the pool from what is still referenced, dropping orphans. Called
  // after the cap trims the oldest snapshots so freed base64 can be GC'd.
  const sweepPool = () => {
    const referenced = new Set();
    const re = new RegExp(SENTINEL + '(\\d+)', 'g');
    stack.forEach((entry) => {
      let m;
      while ((m = re.exec(entry.snap)) !== null) referenced.add(m[1]);
    });
    for (const key of Array.from(srcByKey.keys())) {
      if (!referenced.has(key)) {
        const src = srcByKey.get(key);
        srcByKey.delete(key);
        keyBySrc.delete(src);
      }
    }
  };

  return {
    /**
     * Capture the current canvas state (+ optional `meta`) as a new snapshot,
     * discarding any redo tail. Returns the new index.
     */
    push(meta) {
      const snap = serialize();
      const metaObj = meta || {};
      const metaStr = JSON.stringify(metaObj);
      // Collapse no-op pushes (identical snapshot AND meta) so debounced gestures
      // that ended without a real change don't grow the stack.
      if (index >= 0 && stack[index].snap === snap && stack[index].metaStr === metaStr) return index;
      stack = stack.slice(0, index + 1);
      stack.push({ snap, meta: metaObj, metaStr });
      index = stack.length - 1;
      if (stack.length > cap) {
        const overflow = stack.length - cap;
        stack = stack.slice(overflow);
        index -= overflow;
        sweepPool();
      }
      return index;
    },

    undo() {
      if (index <= 0) return null;
      index -= 1;
      return this.current();
    },

    redo() {
      if (index >= stack.length - 1) return null;
      index += 1;
      return this.current();
    },

    /** Resolve the current entry into { json, meta } for the editor to apply. */
    current() {
      if (index < 0) return null;
      const entry = stack[index];
      return { json: this.resolve(entry.snap), meta: entry.meta };
    },

    /** Inflate a stored snapshot string (sentinels -> real srcs). */
    resolve(snap) {
      if (typeof snap !== 'string') return snap;
      return snap.replace(new RegExp(SENTINEL + '(\\d+)', 'g'), (whole, key) => {
        const src = srcByKey.get(key);
        // JSON.stringify-safe: srcByKey holds a raw string; escape it for JSON.
        return src != null ? escapeForJson(src) : whole;
      });
    },

    canUndo() { return index > 0; },
    canRedo() { return index < stack.length - 1; },
    // index > 0 means the user has moved past the initial baseline = dirty.
    isDirty() { return index > 0; },
    getIndex() { return index; },
    size() { return stack.length; },

    reset() {
      stack = [];
      index = -1;
      keyBySrc.clear();
      srcByKey.clear();
      nextKey = 1;
    },
  };
}

// The sentinel sits inside a JSON string value, so when we substitute the real
// src back we must re-escape it exactly as JSON.stringify would (quotes,
// backslashes, control chars) — data URLs are plain ASCII but be safe.
function escapeForJson(str) {
  const json = JSON.stringify(str);
  // Strip the surrounding quotes JSON.stringify adds; the sentinel was already
  // inside quotes in the snapshot.
  return json.slice(1, -1);
}
